import { ReactElement, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppContext from "src/app/AppContext";
import { DEFAULT_VISITOR_CONTEXT } from "src/trackers/viewerContext";
import LandingHeader from "./LandingHeader";
import LandingBody from "./LandingBody";
import LandingFooter from "./LandingFooter";
import type { LandingLang } from "./constants";
import styles from "./LandingPage.module.scss";

interface LandingPageProps {
  lang: LandingLang;
}

export default function LandingPage({ lang }: LandingPageProps): ReactElement {
  const { i18n } = useTranslation();
  const { visitorContext } = useContext(AppContext);

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [i18n, lang]);

  useEffect(() => {
    // Using a custom event name "page_view" instead of the shared "gaEvent" used by
    // portal pages. Since both landing and portal share the same GTM container,
    // using "gaEvent" would cause GTM to capture landing page views as "pdf portal".
    // A distinct event name allows separate GTM triggers/tags for landing vs portal.
    const visitor = visitorContext ?? DEFAULT_VISITOR_CONTEXT;
    const w = window as unknown as Window & { dataLayer: Record<string, unknown>[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "page_view",
      event_details: {
        pageName: document.title,
        visitorCategory: visitor.visitorCategory,
        visitorId: visitor.visitorId,
        svc: visitor.svc,
      },
    });
  }, []);

  return (
    <div className={styles.landingPage}>
      <LandingHeader />
      <LandingBody lang={lang} />
      <LandingFooter lang={lang} />
    </div>
  );
}
